/**
 * Foundations — search the client's own matched grantmakers.
 *
 * homelab-kb #60 Phase 1. The gap this closes is not subtle: a Pro client has
 * thousands of scored foundation matches and could previously see 25 on the
 * dashboard plus 8 a week in the digest. They were never locked out of the
 * corpus so much as locked out of their own results.
 *
 * Scope is deliberately their EXISTING matches — every result already has a
 * FunderMatch row, the same boundary the profile endpoint enforces. Searching
 * the wider corpus is Phase 2 and needs an ADR first, because that genuinely
 * widens what one account can read.
 */

import React, { useEffect, useRef, useState } from 'react'
import { searchFunderMatches } from '../api'
import { getClientId } from '../session'
import Button from '../components/Button'
import FunderCard from '../components/FunderCard'
import Spinner from '../components/Spinner'
import { ErrorAlert } from '../components/Alert'
import { FOCUS_AREA_OPTIONS, US_STATES } from '../options'
import { useDocumentTitle } from '../components/PublicLayout'

const DEBOUNCE_MS = 350

const EMPTY_FILTERS = {
  q: '', state: '', category: '', min_grant: '', max_grant: '',
  deadline: 'any', sort: 'relevance',
}

// Mirrors the API's DEADLINE_FILTERS. "Year-round" is the largest group in the
// corpus (59% of leads carrying deadline text) and used to render as silence,
// so it earns a filter of its own rather than hiding under "any".
const DEADLINE_OPTIONS = [
  { value: 'any',        label: 'Any deadline' },
  { value: 'dates',      label: 'Has a filed deadline' },
  { value: 'year_round', label: 'Accepts year-round' },
  { value: 'none',       label: 'No deadline stated' },
]

const SORT_OPTIONS = [
  { value: 'relevance',  label: 'Best match' },
  { value: 'name',       label: 'Name (A–Z)' },
  { value: 'grant_size', label: 'Largest typical grant' },
  { value: 'recent',     label: 'Most recent filing' },
]

export default function FoundationSearch() {
  useDocumentTitle('Foundations')
  const clientId = getClientId()

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const debounce = useRef(null)

  function set(field, value) {
    setFilters(f => ({ ...f, [field]: value }))
    setPage(1) // a changed filter invalidates the current page number
  }

  useEffect(() => {
    setLoading(true)
    setError('')
    clearTimeout(debounce.current)
    debounce.current = setTimeout(() => {
      searchFunderMatches({ ...filters, page })
        .then(setData)
        .catch(err => setError(err.message || 'Could not search foundations.'))
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)
    return () => clearTimeout(debounce.current)
  }, [filters, page])

  const total = data?.total ?? 0
  const pageSize = data?.page_size ?? 20
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const filtersActive = Object.keys(EMPTY_FILTERS).some(
    k => filters[k] !== EMPTY_FILTERS[k]
  )

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Foundations</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every grantmaker matched to your organization, searchable. These are
          your own matches, ranked the same way your digest ranks them.
        </p>
      </header>

      <ErrorAlert message={error} />

      <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <input
          type="search"
          value={filters.q}
          onChange={e => set('q', e.target.value)}
          placeholder="Search by foundation name…"
          aria-label="Search by foundation name"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-brand-300"
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">State</span>
            <select value={filters.state} onChange={e => set('state', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">Any state</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">Focus area</span>
            <select value={filters.category} onChange={e => set('category', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">Any focus</option>
              {FOCUS_AREA_OPTIONS.map(o => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">Deadlines</span>
            <select value={filters.deadline} onChange={e => set('deadline', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              {DEADLINE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">Sort</span>
            <select value={filters.sort} onChange={e => set('sort', e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">
              Typical grant at least ($)
            </span>
            <input type="number" min="0" value={filters.min_grant}
              onChange={e => set('min_grant', e.target.value)} placeholder="5000"
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-700">
              …and at most ($)
            </span>
            <input type="number" min="0" value={filters.max_grant}
              onChange={e => set('max_grant', e.target.value)} placeholder="100000"
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
        </div>

        {filtersActive && (
          <button
            onClick={() => { setFilters(EMPTY_FILTERS); setPage(1) }}
            className="text-xs text-brand-500 hover:underline"
          >
            Clear filters
          </button>
        )}
      </section>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {loading
            ? 'Searching…'
            : `${total.toLocaleString()} foundation${total === 1 ? '' : 's'} matched to you`}
        </p>
        {pageCount > 1 && !loading && (
          <p className="text-xs text-gray-500">Page {page} of {pageCount}</p>
        )}
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : total === 0 ? (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-600">
            No foundations match these filters.
          </p>
          {/* Never advise "try a broader keyword" when the filters are the
              cause — that is the dead-end the federal search page used to
              give when its retired SAM.gov filter returned nothing (#40). */}
          <p className="mt-1 text-xs text-gray-500">
            {filtersActive
              ? 'Try clearing a filter. Your matches are ranked, so the best ones appear first with no filters at all.'
              : 'Your foundation matches are still being built. They appear after your first matching run.'}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {data.results.map(funder => (
              <FunderCard
                key={funder.funder_id}
                funder={funder}
                clientId={clientId}
              />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="secondary" size="sm" disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}>
                ← Previous
              </Button>
              <span className="text-sm text-gray-500">{page} / {pageCount}</span>
              <Button variant="secondary" size="sm" disabled={page >= pageCount}
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}>
                Next →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
